
import { mapActions, mapGetters } from 'vuex';

import { REGIONS } from '@/modules/region/domain/region';
import { QUIZ_VARIANTS } from '@/modules/tour/domain/tour';

export default {
  name: 'QuizStart',

  middleware: ['redirect-region'],

  async asyncData({ query, store }) {
    const paramsQuizId = query.quizId || null;
    return { paramsQuizId };
  },

  head() {
    return {
      meta: [
        {
          name: 'robots',
          content: 'noindex',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('quiz', [
      'getQuizVariant',
      'getQuizCurrentIndex',
      'getDefaultStateValue',
      'getQuizReferrerLink',
      'getQuizStepFrom',
      'getProductType',
      'getProductBrand',
    ]),
  },
  mounted() {
    if (this.getQuizStepFrom) {
      window.location.href = this.getQuizReferrerLink || this.$getUrl('home');

      return;
    }

    const experimentVariantTour = this.getTourFromExperiment();
    const tourExists = Object.values(QUIZ_VARIANTS).includes(this.getQuizVariant);

    if (experimentVariantTour) {
      this.setQuizVariantAction(experimentVariantTour);
    } else if (this.paramsQuizId) {
      this.setQuizVariantAction(this.paramsQuizId);
    } else if (!tourExists || this.getQuizVariant === QUIZ_VARIANTS.DEFAULT) {
      if (this.$countryCode !== REGIONS.us) {
        this.setQuizVariantAction(QUIZ_VARIANTS.NUTRITION_MONTHLY_GENERIC_OTHERS);
      } else {
        this.setQuizVariantAction(QUIZ_VARIANTS.NUTRITION_MONTHLY_GENERIC_US);
      }
    }

    this.$nextTick(() => {
      const usedLocale = this.$i18n.locales.find((locale) => locale.code === this.$i18n.locale);
      const language = usedLocale?.acceptedLanguage.substring(0, 2) || 'en';
      const tourId = experimentVariantTour || this.getQuizVariant;
      this.setProductType(tourId);

      this.checkTourParamsForGTM(tourId, language);

      this.$gtm.push({
        tour_id: tourId,
        language,
        app_version: this.$config.appVersion,
        region: this.$countryCode,
        host: this.$host,
        landing_page_path: this.getQuizReferrerLink,
        has_landing_page: this.isLandingPageEventPushed(),
        user_data: {
          address: {
            city: this.$geo?.city,
            postal_code: this.$geo?.postalCode,
            region: this.$geo?.regionCode,
            country: this.$geo?.country,
          },
        },
      });
      const previousUrl = window.document.referrer;
      this.$gtm.push({
        event: 'start_quiz',
        brand: this.getProductBrand,
        value: this.getDefaultStateValue,
        quiz_referrer: previousUrl,
        language,
        app_version: this.$config.appVersion,
        region: this.$countryCode,
        host: this.$host,
        product_type: this.getProductType,
        user_data: {
          address: {
            city: this.$geo?.city,
            postal_code: this.$geo?.postalCode,
            region: this.$geo?.regionCode,
            country: this.$geo?.country,
          },
        },
      });
      this.setLeadUserReferrerLink(previousUrl);
    });

    const quizList = this.setUpQuiz();

    if (!this.getQuizCurrentIndex && this.getQuizCurrentIndex !== 0) {
      this.$sentry.captureMessage(`No quiz current index was found. Redirecting to '/tour'`, {
        level: 'info',
        extra: { tourId: this.getQuizVariant },
      });
      this.$router.push({ path: `/tour` });
      return;
    }

    const currentStepId = quizList[this.getQuizCurrentIndex]?.id;

    if (!currentStepId) {
      this.$sentry.captureMessage(`No step id to navigate to was found. Redirecting to '/tour'`, {
        level: 'info',
        extra: { tourId: this.getQuizVariant, quizList, desiredStepIndex: this.getQuizCurrentIndex },
      });
    }

    const stepToNavigate = currentStepId || 'tour';
    this.$router.push({ path: `/quiz/${stepToNavigate}` });
  },
  methods: {
    ...mapActions('quiz', [
      'setQuizVariantAction',
      'setQuizListAction',
      'setQuizTotalLengthAction',
      'setLeadUserReferrerLink',
      'setProductType',
    ]),
    setUpQuiz() {
      const quizVariants = this.$t(`quizVariants`);
      const quizVariantsKeys = Object.keys(quizVariants);
      const variant = '' + (this.getQuizVariant || this.getUtmObject().utm_source || '');
      const existingVariant = quizVariantsKeys.includes(variant);
      const quizVariantArray = existingVariant
        ? quizVariants[variant].length
          ? quizVariants[variant]
          : quizVariants.default
        : quizVariants.default;
      const quizVariant = quizVariantArray || quizVariants.default;
      const sortFunc = (a, b) => quizVariant.indexOf(a.id) - quizVariant.indexOf(b.id);

      const quizList = this.$t('quiz')
        .filter(({ id }) => quizVariant.includes(id))
        .sort(sortFunc);
      this.setQuizListAction(quizList);
      this.setQuizTotalLengthAction(quizList.length);

      return quizList;
    },
  },
};
